// components/onboarding/OnboardingDecor.jsx
//
// Shared, self-contained decorative + motion primitives that give the onboarding surfaces a premium,
// enterprise feel. Built on framer-motion (already a dependency) plus the app's existing Lottie loader
// asset - no new runtime dependencies or external network assets.

import React from 'react';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const LOADER_SRC = '/animations/loader.lottie';

// App-consistent Lottie loader.
export function LottieLoader({ size = 72, message }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-6">
      <DotLottieReact src={LOADER_SRC} loop autoplay style={{ width: size, height: size }} />
      {message && <p className="text-sm text-gray-500">{message}</p>}
    </div>
  );
}

// Soft, slowly-drifting gradient orbs + faint grid - sits behind content (pointer-events-none).
export function AuroraBackground({ className = '' }) {
  const orbs = [
    { c: 'from-blue-400/30 to-indigo-500/30', size: 460, left: '-8%', top: '-18%', dur: 17 },
    { c: 'from-violet-400/25 to-fuchsia-500/25', size: 380, left: '72%', top: '2%', dur: 21 },
    { c: 'from-cyan-300/25 to-blue-500/25', size: 340, left: '55%', top: '68%', dur: 19 },
  ];
  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      {orbs.map((o, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full bg-gradient-to-br ${o.c} blur-3xl`}
          style={{ width: o.size, height: o.size, left: o.left, top: o.top }}
          animate={{ scale: [1, 1.14, 1], x: [0, 22, 0], y: [0, -18, 0] }}
          transition={{ duration: o.dur, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage: 'linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />
    </div>
  );
}

// A tick that draws itself in - used on success states.
export function AnimatedCheck({ size = 64, className = 'text-green-500' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none" className={className} aria-hidden="true">
      <motion.circle
        cx="26" cy="26" r="24" stroke="currentColor" strokeWidth="3"
        initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      />
      <motion.path
        d="M15 27l7.5 7.5L37 20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.35, ease: 'easeOut' }}
      />
    </svg>
  );
}

// One-shot confetti burst from the centre. Purely decorative.
export function SuccessBurst({ count = 20 }) {
  const colors = ['#3b82f6', '#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4'];
  const parts = Array.from({ length: count }).map((_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 70 + Math.random() * 70;
    return {
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      color: colors[i % colors.length],
      dur: 0.7 + Math.random() * 0.5,
      rot: (Math.random() - 0.5) * 360,
    };
  });
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
      {parts.map((p, i) => (
        <motion.span
          key={i}
          className="absolute h-2 w-2 rounded-[2px]"
          style={{ backgroundColor: p.color }}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
          animate={{ opacity: 0, x: p.x, y: p.y, scale: 0.3, rotate: p.rot }}
          transition={{ duration: p.dur, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
}
